// Dropdown portal for AI Studio desktop option panels

import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

// Simple dropdown manager hook
export const useDropdownManager = () => {
	const [dropdowns, setDropdowns] = useState<Map<string, React.ReactElement>>(new Map());

	const showDropdown = useCallback((key: string, content: React.ReactElement) => {
		setDropdowns((prev) => {
			const next = new Map(prev);
			next.set(key, content);
			return next;
		});
	}, []);

	const closeDropdown = useCallback((key: string) => {
		setDropdowns((prev) => {
			const next = new Map(prev);
			next.delete(key);
			return next;
		});
	}, []);

	return {
		showDropdown,
		closeDropdown,
		dropdowns,
	};
};

// Simple dropdown portal component
interface DropdownPortalProps {
	children: React.ReactNode;
	el: HTMLElement;
	anchor?: 'top' | 'bottom' | 'left' | 'right';
	alignment?: 'left' | 'right' | 'center';
	transparentBg?: boolean;
	onClose: () => void;
}

export const DropdownPortal: React.FC<DropdownPortalProps> = ({
	children,
	el,
	anchor = 'bottom',
	alignment = 'left',
	transparentBg = false,
	onClose,
}) => {
	const portalRoot = document.getElementById('dialogs-portal') || document.body;

	// Calculate position based on trigger element
	const rect = el?.getBoundingClientRect();
	const style: React.CSSProperties = rect
		? {
				position: 'fixed',
				top:
					anchor === 'bottom'
						? `${rect.bottom + 8}px`
						: anchor === 'top'
							? `${rect.top - 8}px`
							: undefined,
				left: alignment === 'left' ? `${rect.left}px` : undefined,
				right: alignment === 'right' ? `${window.innerWidth - rect.right}px` : undefined,
				transform: anchor === 'top' ? 'translateY(-100%)' : undefined,
			}
		: {};

	return createPortal(
		<>
			<Overlay $transparentBg={transparentBg} onClick={onClose} />
			<DropdownContainer style={style}>{children}</DropdownContainer>
		</>,
		portalRoot
	);
};

const Overlay = styled.div<{ $transparentBg?: boolean }>`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: ${(props) => (props.$transparentBg ? 'transparent' : 'rgba(0, 0, 0, 0.3)')};
	z-index: 10000;
`;

const DropdownContainer = styled.div`
	position: fixed;
	z-index: 10001;
	background: white;
	border-radius: 8px;
	box-shadow:
		0 4px 6px -1px rgba(0, 0, 0, 0.1),
		0 2px 4px -1px rgba(0, 0, 0, 0.06);
	max-width: calc(100vw - 16px);
`;
